import { Attachment, AttachmentStatus, RequiredFileUploaderOptions } from '../file-uploader-types';
import defaultWarnings from '../warnings/default-warnings';
import {
    HAVE_ALREADY_CHOOSEN_FILE,
    FILE_IS_TOO_LARGE,
    FILE_IS_TOO_SMALL,
    FILE_TYPE_NOT_ALLOWED,
    TOTAL_FILE_SIZE_TOO_LARGE,
    FILE_ERROR,
} from '../warnings/warning-types';

export const calculateFileSize = (fileSizes: number[]) => fileSizes.reduce((a, b) => a + b, 0);

export const getFiltype = (attachment: File) => {
    const startindexFiltype = attachment && attachment.name.lastIndexOf('.');
    return startindexFiltype ? attachment.name.substring(startindexFiltype + 1, attachment.name.length) : '';
};

export const fileTooLarge = (fil: File, options: RequiredFileUploaderOptions): boolean => {
    return fil.size >= options.maxFileByteSize;
};

export const fileTooSmall = (
    attachment: File,
    { largeFormatFileTypes, largeFormatFileMinByteSize }: RequiredFileUploaderOptions,
) =>
    largeFormatFileTypes.includes(getFiltype(attachment))
        ? attachment.size < largeFormatFileMinByteSize
        : attachment.size < 1;

const attachmentHasFailedBefore = (status: AttachmentStatus): boolean =>
    status === AttachmentStatus.Error || status === AttachmentStatus.ErrorTotalFileSize;

export const fileIsDuplicate = (fileName: string, attachments: Attachment[]): boolean =>
    attachments.some((attachment) => !attachmentHasFailedBefore(attachment.status) && attachment.fileName === fileName);

export const isAllowedFiltype = (attachment: File, { allowedFileTypes }: RequiredFileUploaderOptions) => {
    return allowedFileTypes.includes(getFiltype(attachment).toLowerCase());
};

export const totalFileSizeToLarge = (
    attachments: Attachment[],
    attachmentsToUpload: Attachment[],
    options: RequiredFileUploaderOptions,
): boolean =>
    calculateFileSize(attachments.map((tmp) => tmp.size)) +
        calculateFileSize(attachmentsToUpload.map((tmp) => tmp.size)) >
    options.maxCombinedUploadByteSize;

export const validateAttachmentsToUpload = (
    attachmentsToUpload: Attachment[],
    attachments: Attachment[],
    options: RequiredFileUploaderOptions,
) =>
    attachmentsToUpload.filter((attachment: Attachment) => {
        return !(
            fileIsDuplicate(attachment.fileName, attachments) ||
            fileTooLarge(attachment.file, options) ||
            fileTooSmall(attachment.file, options) ||
            !isAllowedFiltype(attachment.file, options) ||
            totalFileSizeToLarge(attachments, attachmentsToUpload, options)
        );
    });

export const getWarnings = (
    options: RequiredFileUploaderOptions,
    attachments: Attachment[],
    attachmentsToUpload: Attachment[] = [],
    isUpload = false,
) => {
    const warnings = defaultWarnings(options);

    attachmentsToUpload.forEach((attachment: Attachment) => {
        if (fileTooLarge(attachment.file, options)) warnings[FILE_IS_TOO_LARGE].fileName.push(attachment.fileName);
        if (fileIsDuplicate(attachment.fileName, attachments))
            warnings[HAVE_ALREADY_CHOOSEN_FILE].fileName.push(attachment.fileName);
        if (fileTooSmall(attachment.file, options)) warnings[FILE_IS_TOO_SMALL].fileName.push(attachment.fileName);
        if (!isAllowedFiltype(attachment.file, options))
            warnings[FILE_TYPE_NOT_ALLOWED].fileName.push(attachment.fileName);
    });

    if (!isUpload) {
        attachments.forEach((f) => {
            if (f.status === AttachmentStatus.Error) warnings[FILE_ERROR].fileName.push(f.fileName);
            if (f.status === AttachmentStatus.ErrorTotalFileSize) {
                warnings[FILE_IS_TOO_LARGE].fileName.push(f.fileName);
            }
        });
    }

    if (totalFileSizeToLarge(attachmentsToUpload, attachments, options)) {
        warnings[TOTAL_FILE_SIZE_TOO_LARGE].fileName.push(...attachmentsToUpload.map((fil) => fil.fileName));
    }

    return warnings;
};
